import * as THREE from 'three';
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

interface FbxToolDefinition {
  readonly model: string;
  readonly baseColor: string;
  readonly normal: string;
  readonly roughness: string;
}

const RUSTY_SHOVEL: FbxToolDefinition = {
  model: '/assets/tools/rusty-shovel/Rusty_Shovel_uddjeelqx_High.fbx',
  baseColor: '/assets/tools/rusty-shovel/basecolor.jpg',
  normal: '/assets/tools/rusty-shovel/normal.jpg',
  roughness: '/assets/tools/rusty-shovel/roughness.jpg',
};
const OLD_PICKAXE: FbxToolDefinition = {
  model: '/assets/tools/old-pickaxe/Old_Pickaxe_ucylebxfa_High.fbx',
  baseColor: '/assets/tools/old-pickaxe/basecolor.jpg',
  normal: '/assets/tools/old-pickaxe/normal.jpg',
  roughness: '/assets/tools/old-pickaxe/roughness.jpg',
};

export class ToolAssetLoader {
  private readonly fbx = new FBXLoader();
  private readonly gltf = new GLTFLoader();
  private readonly textures = new THREE.TextureLoader();
  private readonly cache = new Map<string, THREE.Group>();

  async loadForTier(tier: number): Promise<THREE.Group | null> {
    if (tier <= 1) return null;
    if (tier <= 3) return this.loadFbx('rusty-shovel', RUSTY_SHOVEL);
    if (tier <= 6) return this.loadFbx('old-pickaxe', OLD_PICKAXE);
    return this.loadGlb('titan-drill', '/assets/tools/titan-drill/titan-drill.glb');
  }

  private async loadFbx(cacheKey: string, definition: FbxToolDefinition): Promise<THREE.Group> {
    const cached = this.cache.get(cacheKey);
    if (cached) return cached.clone(true);
    const [model, baseColor, normal, roughness] = await Promise.all([
      this.fbx.loadAsync(definition.model),
      this.textures.loadAsync(definition.baseColor),
      this.textures.loadAsync(definition.normal),
      this.textures.loadAsync(definition.roughness),
    ]);
    baseColor.colorSpace = THREE.SRGBColorSpace;
    const material = new THREE.MeshStandardMaterial({ map: baseColor, normalMap: normal, roughnessMap: roughness, roughness: .78, metalness: .08, side: THREE.DoubleSide });
    model.traverse(child => {
      if (!(child instanceof THREE.Mesh)) return;
      child.material = material;
      child.castShadow = true;
      child.receiveShadow = true;
    });
    if (cacheKey === 'rusty-shovel') model.rotation.x = Math.PI / 2;
    const normalized = this.normalize(model, cacheKey === 'rusty-shovel' ? .86 : 1.12);
    this.cache.set(cacheKey, normalized);
    return normalized.clone(true);
  }

  private async loadGlb(cacheKey: string, path: string): Promise<THREE.Group> {
    const cached = this.cache.get(cacheKey);
    if (cached) return cached.clone(true);
    const result = await this.gltf.loadAsync(path);
    result.scene.traverse(child => {
      if (!(child instanceof THREE.Mesh)) return;
      child.castShadow = true;
      child.receiveShadow = true;
    });
    const normalized = this.normalize(result.scene, 1.18);
    normalized.rotation.x += .08;
    this.cache.set(cacheKey, normalized);
    return normalized.clone(true);
  }

  private normalize(source: THREE.Object3D, targetLength: number): THREE.Group {
    const wrapper = new THREE.Group();
    wrapper.add(source);
    source.updateMatrixWorld(true);
    let box = new THREE.Box3().setFromObject(source);
    let size = box.getSize(new THREE.Vector3());
    // Standardize every hand tool so its working head points along local +X.
    // The supplied pickaxe head is at +Y, so the previous positive quarter-turn
    // put its head on the wrong end of the first-person pose.
    if (size.y >= size.x && size.y >= size.z) source.rotation.z = -Math.PI / 2;
    else if (size.z >= size.x && size.z >= size.y) source.rotation.y = Math.PI / 2;
    source.updateMatrixWorld(true);
    box = new THREE.Box3().setFromObject(source);
    size = box.getSize(new THREE.Vector3());
    const scale = targetLength / Math.max(size.x, size.y, size.z, .001);
    source.scale.multiplyScalar(scale);
    source.updateMatrixWorld(true);
    box = new THREE.Box3().setFromObject(source);
    const center = box.getCenter(new THREE.Vector3());
    source.position.sub(center);
    source.position.x -= .22;
    wrapper.name = 'LoadedToolAsset';
    return wrapper;
  }
}
